import { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { apiCallable } from '@/lib/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Clock, Camera, User } from 'lucide-react'
import { formatDateTime } from '@/lib/dateTime'
import { cn } from '@/lib/utils'

interface Face {
  personId: string | null
  personName: string
  classification: string
  classificationId: string
  bbox: { x: number; y: number; width: number; height: number }
  isNewFace: boolean
  isUnmatched?: boolean
  similarity: number | null
  detScore?: number | null
  qualityScore?: number | null
}

interface SightingDetail {
  id: string
  cameraId: string
  deviceId: string
  deviceName: string
  cameraName?: string
  timestamp: string
  frameUrl: string
  faces: Array<Face & { classification?: { id: string; name: string; displayName: string; color: string } }>
  metadata: {
    totalFaces: number
    alertSent: boolean
    hasNewFaces: boolean
    hasUnmatchedFaces?: boolean
  }
}

interface SightingDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  sightingId: string | null
  groupId: string
  /** Opens the person drawer over this dialog rather than routing away from it. */
  onNavigateToPerson: (personId: string) => void
  /**
   * True while the person drawer is open on top. Drops this dialog below the
   * drawer's layer (veil z-50 / card z-60) so the drawer's backdrop dims it,
   * instead of two same-z layers fighting over paint order.
   */
  stacked?: boolean
  /** Bumped when a person is edited elsewhere, to re-read the person names here. */
  refreshKey?: number
}

export function SightingDetailDialog({
  open,
  onOpenChange,
  sightingId,
  groupId,
  onNavigateToPerson,
  stacked = false,
  refreshKey = 0,
}: SightingDetailDialogProps) {
  const { t } = useTranslation()
  const [sighting, setSighting] = useState<SightingDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [classifications, setClassifications] = useState<Map<string, { color: string; displayName: string }>>(new Map())

  // Load classifications
  useEffect(() => {
    const loadClassifications = async () => {
      try {
        const getClassifications = apiCallable('getClassifications')
        const result = await getClassifications({ groupId })
        const data = result.data as { success: boolean; classifications: any[] }

        if (data.success) {
          const classMap = new Map()
          data.classifications.forEach((c: any) => {
            classMap.set(c.id, { color: c.color, displayName: c.displayName })
          })
          setClassifications(classMap)
        }
      } catch (err) {
        console.error('Failed to load classifications:', err)
      }
    }

    if (open) {
      loadClassifications()
    }
  }, [groupId, open])

  // Load sighting details
  useEffect(() => {
    const loadSighting = async () => {
      if (!sightingId || !groupId) return

      setLoading(true)
      setError(null)

      try {
        const getSightingDetails = apiCallable('getSightingDetails')
        const result = await getSightingDetails({ groupId, sightingId })
        const data = result.data as { success: boolean; sighting: SightingDetail }

        if (data.success) {
          setSighting(data.sighting)
        } else {
          setError(t('sightings.failedToLoad'))
        }
      } catch (err) {
        console.error('Failed to load sighting:', err)
        setError(t('sightings.failedToLoadDetails'))
      } finally {
        setLoading(false)
      }
    }

    if (open && sightingId) {
      loadSighting()
    } else {
      setSighting(null)
    }
  }, [open, sightingId, groupId, refreshKey])

  // Draw annotations when sighting loads
  useEffect(() => {
    if (!sighting || !canvasRef.current) return

    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      canvas.width = img.width
      canvas.height = img.height
      ctx.drawImage(img, 0, 0)

      // Draw face bounding boxes
      sighting.faces.forEach(face => {
        if (face.isUnmatched) {
          // Dashed gray bounding box for unmatched faces
          ctx.strokeStyle = '#888888'
          ctx.lineWidth = 2
          ctx.setLineDash([6, 4])
          ctx.strokeRect(face.bbox.x, face.bbox.y, face.bbox.width, face.bbox.height)
          ctx.setLineDash([])

          const label = 'Unidentified'
          ctx.font = 'italic bold 16px sans-serif'
          const textWidth = ctx.measureText(label).width
          ctx.fillStyle = '#888888'
          ctx.fillRect(face.bbox.x, face.bbox.y - 26, textWidth + 12, 26)
          ctx.fillStyle = '#ffffff'
          ctx.fillText(label, face.bbox.x + 6, face.bbox.y - 7)
          ctx.font = 'bold 16px sans-serif'
        } else {
          const classInfo = face.classification || classifications.get(face.classificationId)
          const color = classInfo?.color || '#999999'

          ctx.strokeStyle = color
          ctx.lineWidth = 3
          ctx.strokeRect(face.bbox.x, face.bbox.y, face.bbox.width, face.bbox.height)

          // Draw label background
          const label = face.personName || 'Unknown'
          ctx.font = 'bold 16px sans-serif'
          const textWidth = ctx.measureText(label).width
          ctx.fillStyle = color
          ctx.fillRect(face.bbox.x, face.bbox.y - 26, textWidth + 12, 26)

          // Draw label text
          ctx.fillStyle = '#ffffff'
          ctx.fillText(label, face.bbox.x + 6, face.bbox.y - 7)
        }
      })
    }
    img.onerror = () => {
      console.error('Failed to load image:', sighting.frameUrl)
    }
    if (sighting.frameUrl) {
      img.src = sighting.frameUrl
    }
  }, [sighting, classifications])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn('max-w-4xl max-h-[90vh] overflow-y-auto', stacked && 'z-[40]')}
        overlayClassName={stacked ? 'z-[30]' : undefined}
      >
        <DialogHeader>
          <DialogTitle>{t('sightings.sightingDetails')}</DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="py-12 flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : error ? (
          <div className="py-12">
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          </div>
        ) : sighting ? (
          <div className="space-y-6">
            {/* Image with annotations */}
            <div className="relative bg-black rounded-lg overflow-hidden">
              <canvas
                ref={canvasRef}
                className="w-full h-auto"
              />
              {sighting.metadata.alertSent && (
                <Badge
                  variant="destructive"
                  className="absolute top-3 right-3"
                >
                  {t('sightings.alertSent')}
                </Badge>
              )}
            </div>

            {/* Metadata */}
            <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>{formatDateTime(sighting.timestamp)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4" />
                <span>{sighting.cameraName || sighting.deviceName}</span>
              </div>
              {sighting.cameraName && sighting.cameraName !== sighting.deviceName && (
                <div className="text-xs">
                  {t('sightings.on', { device: sighting.deviceName })}
                </div>
              )}
            </div>

            {/* Detected persons */}
            <div className="space-y-3">
              <h3 className="font-semibold">{t('sightings.detectedPersons', { count: sighting.faces.length })}</h3>
              <div className="grid gap-3">
                {sighting.faces.map((face, idx) => {
                  if (face.isUnmatched || !face.personId) {
                    return (
                      <div
                        key={idx}
                        className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className="w-3 h-3 rounded-full border border-dashed border-muted-foreground"
                          />
                          <div>
                            <div className="font-medium italic text-muted-foreground">{t('sightings.unidentified')}</div>
                            <div className="text-xs text-muted-foreground">
                              {t('sightings.noMatchingPerson')}
                            </div>
                          </div>
                        </div>
                        <Badge variant="outline" className="text-muted-foreground">
                          {t('sightings.noMatch')}
                        </Badge>
                      </div>
                    )
                  }
                  const classInfo = face.classification || classifications.get(face.classificationId)
                  return (
                    <div
                      key={idx}
                      className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: classInfo?.color || '#999' }}
                        />
                        <div>
                          <div className="font-medium">{face.personName || t('common.unknown')}</div>
                          <div className="text-xs text-muted-foreground">
                            {classInfo?.displayName || face.classification || t('common.unknown')}
                            {face.similarity && ` - ${t('sightings.match', { percent: (face.similarity * 100).toFixed(0) })}`}
                            {face.isNewFace && ' ' + t('sightings.new')}
                          </div>
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onNavigateToPerson(face.personId!)}
                      >
                        <User className="h-4 w-4 mr-1" />
                        {t('sightings.viewPerson')}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        ) : (
          <div className="py-12 text-center text-muted-foreground">
            {t('sightings.sightingNotFound')}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
